/**
 * useBackdropDismissal — when a press on a dialog's backdrop dismisses it (PLANNER-PALM-UAT-2026-11-03-001 U4).
 *
 * The owner's report: "when working in the shift creation modal … if the user swipes their mouse outside the modal
 * the modal disappears". Measured live: a press that starts INSIDE the dialog (selecting a time, a trackpad drag) and
 * is released over the backdrop makes the browser fire `click` on the nearest common ancestor of the down and up
 * targets — the backdrop itself — so a handler that judged only `event.target === event.currentTarget` dismissed a
 * dialog the person was still working in.
 *
 * So the PRESS decides, not the click: the backdrop dismisses only when the pointer went down AND came up on the
 * backdrop itself. Both are read in the CAPTURE phase, so no handler inside the dialog can hide a press from it by
 * stopping propagation. A click that arrives with no recorded press (a synthetic click, an assistive technology's
 * activation) is judged by its target alone, as before. Each click consumes the record, so one swipe never poisons the
 * next genuine backdrop click.
 *
 * A DRAWER's backdrop is a sibling of its panel, not its parent: a press in the panel never reaches it, and its click
 * (on the common ancestor) never targets it — the same props are correct there too.
 *
 * Its own module because `useModalChrome` sits at its structural hooks budget (`web/excessive-hooks-usage`, which may
 * not be suppressed); the chrome composes this and hands its result out as `backdropProps`.
 *
 * @module @puffin-core/dialog-chrome/useBackdropDismissal
 */

import { useCallback, useMemo, useRef } from 'react';
import { DISMISSAL_BACKDROP } from './dialogStack.js';

/**
 * The three handlers a backdrop element carries, typed as the element's own event handlers so they spread onto it.
 *
 * @typedef {object} BackdropProps
 * @property {React.PointerEventHandler<HTMLElement>} onPointerDownCapture - Records where the press went down.
 * @property {React.PointerEventHandler<HTMLElement>} onPointerUpCapture - Records where the press came up.
 * @property {React.MouseEventHandler<HTMLElement>} onClick - Dismisses only a press that went down AND came up on the
 *   backdrop itself.
 */

/**
 * @param {Object} params
 * @param {boolean} params.closeOnBackdrop - `false` for a dialog that must not be dismissed from its backdrop.
 * @param {Function} [params.onClose] - Called with `DISMISSAL_BACKDROP` when the backdrop dismisses.
 * @returns {BackdropProps} spread onto the backdrop element (`<DialogScrim backdropProps={…}>`, or
 *   `{...backdropProps}` on a hand-placed backdrop).
 */
export function useBackdropDismissal({ closeOnBackdrop, onClose }) {
  // `null` = no press recorded since the last click; otherwise where the press went down and came up.
  const pressRef = useRef(null);

  const onPointerDownCapture = useCallback((event) => {
    pressRef.current = { down: event.target === event.currentTarget, up: false };
  }, []);

  const onPointerUpCapture = useCallback((event) => {
    if (pressRef.current) pressRef.current.up = event.target === event.currentTarget;
  }, []);

  const onClick = useCallback(
    (event) => {
      const press = pressRef.current;
      pressRef.current = null;
      if (press && !(press.down && press.up)) return;
      if (closeOnBackdrop && event.target === event.currentTarget && typeof onClose === 'function') {
        // Named, like the stack's Escape path: two pos-web AUTH surfaces write the dismissal METHOD to an audit
        // trail, and a source-blind `onClose()` collapses them into one.
        onClose(DISMISSAL_BACKDROP);
      }
    },
    [closeOnBackdrop, onClose],
  );

  return useMemo(
    () => ({ onPointerDownCapture, onPointerUpCapture, onClick }),
    [onPointerDownCapture, onPointerUpCapture, onClick],
  );
}

export default useBackdropDismissal;
